import React from 'react'
import type { Content, ContentStatus } from './types'
import { STATUS_PALETTE, STATUS_CONFIG } from './types'

type ContentCardProps = {
  content: Content
  isWorking: boolean
  workingAction?: string
  onViewDetail: () => void
  onReindex: () => void
  onForceTranscribe: () => void
  onDelete: () => void
}

/**
 * Carte de contenu avec statut animé et actions
 * Responsabilité unique : affichage d'un contenu
 */
export default function ContentCard({
  content,
  isWorking,
  workingAction,
  onViewDetail,
  onReindex,
  onForceTranscribe,
  onDelete,
}: ContentCardProps) {
  const statusConfig = STATUS_CONFIG[content.status] || STATUS_CONFIG.uploaded
  const statusClass = STATUS_PALETTE[content.status] || 'bg-zinc-100 text-zinc-600'
  const isProcessing = content.status === 'transcribing' || content.status === 'indexing'
  const canForceTranscribe = content.status === 'uploaded' || content.status === 'error'

  // Déterminer l'icône du type de contenu basé sur le titre ou autres indices
  const getContentIcon = () => {
    const title = content.title.toLowerCase()
    if (title.includes('video') || title.includes('vidéo') || title.match(/\.(mp4|mov|avi|mkv)$/i)) {
      return '🎥'
    }
    if (title.match(/\.(mp3|wav|ogg|m4a|flac)$/i) || title.includes('audio') || title.includes('podcast')) {
      return '🎵'
    }
    if (title.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) || title.includes('image')) {
      return '🖼️'
    }
    return '📄'
  }

  return (
    <div className="group flex items-center justify-between rounded-2xl border border-zinc-200/70 bg-white/80 p-4 transition hover:border-emerald-200 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900/80 dark:hover:border-emerald-800">
      {/* Partie gauche : icône + infos */}
      <div className="flex items-center gap-4 min-w-0 flex-1">
        {/* Icône type */}
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg ${
          isProcessing 
            ? 'bg-blue-100 dark:bg-blue-900/40' 
            : content.status === 'error'
              ? 'bg-red-100 dark:bg-red-900/40'
              : 'bg-emerald-100 dark:bg-emerald-900/40'
        }`}>
          {isProcessing ? (
            <svg className="h-5 w-5 animate-spin text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            getContentIcon()
          )}
        </div>

        {/* Infos */}
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-semibold text-zinc-800 dark:text-zinc-100">
            {content.title}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
            <span>ID {content.id}</span>
            {content.course_title && (
              <>
                <span className="text-zinc-300 dark:text-zinc-600">•</span>
                <span className="truncate max-w-[150px]">{content.course_title}</span>
              </>
            )}
            {content.last_indexed_at && (
              <>
                <span className="text-zinc-300 dark:text-zinc-600">•</span>
                <span>Indexé {formatDate(content.last_indexed_at)}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Partie droite : statut + actions */}
      <div className="flex items-center gap-3 shrink-0 ml-4">
        {/* Badge statut */}
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}>
          {isProcessing && (
            <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {statusConfig.label}
        </span>

        {/* Actions */}
        <div className={`flex gap-1 ${isWorking ? 'opacity-50 pointer-events-none' : ''}`}>
          {/* Détails */}
          <button
            type="button"
            onClick={onViewDetail}
            disabled={isWorking}
            className="rounded-full border border-zinc-200/70 p-2 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-700 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
            title="Voir détails"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>

          {/* Ré-indexer */}
          <button
            type="button"
            onClick={onReindex}
            disabled={isWorking}
            className="rounded-full border border-zinc-200/70 p-2 text-zinc-500 transition hover:bg-emerald-50 hover:text-emerald-600 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-emerald-900/40 dark:hover:text-emerald-400"
            title="Ré-indexer"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          {/* Forcer transcription (si applicable) */}
          {canForceTranscribe && (
            <button
              type="button"
              onClick={onForceTranscribe}
              disabled={isWorking}
              className="rounded-full border border-zinc-200/70 p-2 text-zinc-500 transition hover:bg-blue-50 hover:text-blue-600 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-blue-900/40 dark:hover:text-blue-400"
              title="Forcer transcription"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </button>
          )}

          {/* Supprimer */}
          <button
            type="button"
            onClick={onDelete}
            disabled={isWorking}
            className="rounded-full border border-zinc-200/70 p-2 text-zinc-500 transition hover:bg-red-50 hover:text-red-600 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-red-900/40 dark:hover:text-red-400"
            title="Supprimer"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

function formatDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) {
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  }
  if (diffDays === 1) {
    return 'hier'
  }
  if (diffDays < 7) {
    return `il y a ${diffDays}j`
  }
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}
